import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Filters } from './types';

/**
 * Os filtros vivem na URL (PRD §4: "estado ativo persiste na URL"), não em
 * state local — assim recarregar, compartilhar link e voltar no histórico
 * preservam exatamente a mesma tela.
 */
const KEYS = [
  'from', 'to', 'date_field', 'zone', 'product_category', 'plan_frequency',
  'source_system', 'event_type', 'audit_status', 'reason_id', 'is_exception',
  'channel', 'partner_name',
] as const;

export type FilterKey = (typeof KEYS)[number];

export function useFilters() {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<Filters>(() => {
    const f: Record<string, unknown> = {};
    for (const k of KEYS) {
      const v = params.get(k);
      if (v === null || v === '') continue;
      if (k === 'reason_id') f[k] = Number(v);
      else if (k === 'is_exception') f[k] = v === 'true';
      else f[k] = v;
    }
    return f as Filters;
  }, [params]);

  const setFilter = useCallback((key: FilterKey, value: string | number | boolean | undefined) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === undefined || value === '' || value === null) next.delete(key);
      else next.set(key, String(value));
      return next;
    }, { replace: true });
  }, [setParams]);

  /** Período é sempre um par — setar de uma vez evita dois re-renders e um estado intermediário inválido. */
  const setRange = useCallback((from?: string, to?: string) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (from) next.set('from', from); else next.delete('from');
      if (to) next.set('to', to); else next.delete('to');
      return next;
    }, { replace: true });
  }, [setParams]);

  // Canal é contexto da tela (Direto/Parceiros), não um filtro — "limpar" não tira o usuário do canal.
  const clearAll = useCallback(() => {
    setParams((prev) => {
      const next = new URLSearchParams();
      const channel = prev.get('channel');
      if (channel) next.set('channel', channel);
      return next;
    }, { replace: true });
  }, [setParams]);

  const activeCount = KEYS.filter((k) => params.get(k) && k !== 'date_field' && k !== 'channel').length;

  return { filters, setFilter, setRange, clearAll, activeCount };
}
